import { useCallback, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Clock,
  Handshake,
  MapPin,
  Shield,
} from "lucide-react";
import PageMeta from "@/components/PageMeta";
import SectionFadeIn from "@/components/SectionFadeIn";
import { trackCategoryClick } from "@/lib/analytics";
import { getRouteState } from "@/lib/navigation";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/4a60d089-16ce-43a5-b375-972c21245635/images/a22284d0d626e039da87579bc3612ce22b6348351b9c17856af2509cc31cc779.png";
const STUDIO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/4a60d089-16ce-43a5-b375-972c21245635/images/3e1617346df6f48aa236473944528e05b0948b362c1b4d09145f6ba216c21cc6.png";
const CUSTOM_IMG =
  "https://static.prod-images.emergentagent.com/jobs/4a60d089-16ce-43a5-b375-972c21245635/images/0201f99be3194a155bf43dba77a5a9c22bdee8afeb9d262f16b06ac5359457eb.png";

// ── Animation prop constants (extracted from inline JSX to avoid re-creating on every render) ──
const FADE_FROM_36 = { opacity: 0, y: 36 };
const FADE_FROM_28 = { opacity: 0, y: 28 };
const FADE_FROM_26 = { opacity: 0, y: 26 };
const FADE_FROM_18 = { opacity: 0, y: 18 };
const FADE_TO_VISIBLE = { opacity: 1, y: 0 };
const VIEWPORT_ONCE_30 = { once: true, amount: 0.3 };
const VIEWPORT_ONCE_35 = { once: true, amount: 0.35 };
const TRANSITION_HERO = { duration: 0.8, ease: "easeOut" };
const TRANSITION_HERO_CTA = { duration: 0.7, ease: "easeOut", delay: 0.18 };
const TRANSITION_TRUST = { duration: 0.8, ease: "easeOut", delay: 0.28 };
const ZOOM_TRANSITION_STYLE = { transitionDuration: "1400ms" };

// Index-driven transitions are built via small factories — not memoized because
// they're trivial, but extracted so the JSX call sites stay clean.
const galleryCardTransition = (index) => ({ duration: 0.7, delay: index * 0.08 });
const processStepTransition = (index) => ({ duration: 0.6, delay: index * 0.08 });

const categories = [
  {
    title: "Canvas Prints",
    eyebrow: "For interiors",
    desc: "Gallery-scale artwork for living spaces, hospitality projects, and reception areas that need depth without visual noise.",
    href: "/canvas-category-landing",
    cta: "Browse canvas",
    img: "https://images.unsplash.com/photo-1758448755778-90ebf4d0f1e7?w=1200&q=80",
  },
  {
    title: "Wall Murals",
    eyebrow: "For statement walls",
    desc: "Made-to-measure murals designed to set the mood of a room from the first glance to the final install.",
    href: "/wall-murals-landing",
    cta: "See mural finishes",
    img: "https://images.unsplash.com/photo-1709544506583-cfb9845cdfb9?w=1200&q=80",
  },
  {
    title: "Commercial Graphics",
    eyebrow: "For rollout work",
    desc: "Vehicle graphics, floor vinyls, banners, and branded print systems produced to brief when the space needs to perform.",
    href: "/custom-order-landing",
    cta: "Start a project",
    img: CUSTOM_IMG,
  },
];

const quickLinks = [
  { label: "Stickers & Labels", href: "/stickers-labels" },
  { label: "Banners", href: "/banners" },
  { label: "Floor Graphics", href: "/floor-graphics" },
  { label: "Vehicle Graphics", href: "/vehicle-graphics" },
];

const trust = [
  { icon: MapPin, label: "Printed in Coleraine" },
  { icon: Shield, label: "Premium materials" },
  { icon: Clock, label: "5-7 day turnaround" },
  { icon: Handshake, label: "Bespoke guidance" },
];

const processSteps = [
  {
    number: "01",
    title: "Start with the space",
    copy: "Bring the room, wall, vehicle, or site brief first. We match scale, finish, and installation route around where the work will live.",
  },
  {
    number: "02",
    title: "Choose the right material",
    copy: "Canvas, mural stock, floor vinyl, banners, and labels are selected for use-case, durability, and the way the final surface should feel.",
  },
  {
    number: "03",
    title: "Print, finish, install",
    copy: "Large-format production is handled locally on Roland TrueVIS equipment, then finished cleanly so the end result reads as fitted rather than temporary.",
  },
];

const GALLERY_SCROLL_ID = "gallery-track";

function GalleryNav() {
  const scroll = (dir) => {
    const el = document.getElementById(GALLERY_SCROLL_ID);
    if (!el) return;
    const card = el.querySelector("[data-gallery-card]");
    const width = card ? card.getBoundingClientRect().width + 24 : 380;
    el.scrollBy({ left: dir * width, behavior: "smooth" });
  };

  return (
    <div className="hidden md:flex items-center gap-2" data-testid="gallery-nav">
      <button
        onClick={() => scroll(-1)}
        className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-brand-text-secondary transition-colors duration-300 hover:border-brand-gold hover:text-brand-gold"
        aria-label="Previous"
        data-testid="gallery-prev"
      >
        <ChevronLeft size={18} />
      </button>
      <button
        onClick={() => scroll(1)}
        className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-brand-text-secondary transition-colors duration-300 hover:border-brand-gold hover:text-brand-gold"
        aria-label="Next"
        data-testid="gallery-next"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

function GalleryCarousel({ location }) {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  const handleScroll = useCallback(() => {
    // No dependencies needed: reads `trackRef.current` (refs are stable and excluded
    // from deps by React) and uses only local variables inside the effect.
    const el = trackRef.current;
    if (!el) return;
    const cards = el.querySelectorAll("[data-gallery-card]");
    let closest = 0;
    let minDistance = Infinity;

    cards.forEach((card, index) => {
      const distance = Math.abs(
        card.getBoundingClientRect().left - el.getBoundingClientRect().left
      );
      if (distance < minDistance) {
        minDistance = distance;
        closest = index;
      }
    });

    setActive(closest);
  }, []);

  return (
    <>
      <div
        ref={trackRef}
        id={GALLERY_SCROLL_ID}
        onScroll={handleScroll}
        className="flex gap-6 overflow-x-auto px-6 pb-4 md:px-12 no-scrollbar scroll-smooth snap-x snap-mandatory"
        data-testid="gallery-track"
      >
        {categories.map((category, index) => (
          <motion.div
            key={category.title}
            initial={FADE_FROM_36}
            whileInView={FADE_TO_VISIBLE}
            viewport={VIEWPORT_ONCE_30}
            transition={galleryCardTransition(index)}
            className="flex-none w-[84vw] sm:w-[68vw] md:w-[34rem] lg:w-[36rem] snap-start"
          >
            <Link
              to={category.href}
              state={getRouteState(location)}
              data-gallery-card
              className="group relative block overflow-hidden"
              data-testid={`category-${category.title.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => trackCategoryClick(category.title, category.href)}
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-brand-surface">
                <img
                  src={category.img}
                  alt={category.title}
                  className="h-full w-full object-cover transition-transform ease-out group-hover:scale-[1.05]"
                  style={ZOOM_TRANSITION_STYLE}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#05060A] via-[#05060A]/35 to-transparent" />
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-gold/80 to-transparent" />
              </div>

              <div className="absolute inset-0 flex flex-col justify-between px-6 py-6 md:px-8 md:py-8">
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.3em] text-white/68">
                      {category.eyebrow}
                    </p>
                    <h3 className="mt-3 max-w-[12ch] font-heading text-[2rem] leading-[0.92] tracking-tight text-white md:text-[2.4rem]">
                      {category.title}
                    </h3>
                  </div>
                  <span className="font-heading text-[3rem] leading-none text-white/18 md:text-[4rem]">
                    0{index + 1}
                  </span>
                </div>

                <div className="max-w-sm">
                  <p className="text-sm leading-relaxed text-white/70 md:text-[15px]">
                    {category.desc}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.26em] text-brand-gold transition-all duration-300 group-hover:gap-3">
                    {category.cta} <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="mt-5 flex justify-center gap-2 md:hidden" data-testid="gallery-dots">
        {categories.map((category, index) => (
          <span
            key={category.title}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              active === index ? "w-7 bg-brand-gold" : "w-1.5 bg-white/18"
            }`}
          />
        ))}
      </div>
    </>
  );
}

export default function HomePage() {
  const location = useLocation();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const glowOpacity = useTransform(scrollYProgress, [0, 1], [0.26, 0.58]);
  // Memoize the style objects that wrap MotionValues so we don't recreate them
  // on every render. The MotionValue refs are stable, so the deps are stable too.
  const heroBgStyle = useMemo(() => ({ scale: heroScale, y: heroY }), [heroScale, heroY]);
  const heroGlowStyle = useMemo(() => ({ opacity: glowOpacity }), [glowOpacity]);

  return (
    <>
      <PageMeta
        title="Premium Print & Signage"
        description="Canvas prints, wall murals, and large-format graphics designed for interiors, retail, and commercial spaces. Printed locally in Coleraine with a configure-first, quote-led approach."
      />

      <section
        ref={heroRef}
        className="relative isolate overflow-hidden bg-[#05060A]"
        data-testid="hero-section"
      >
        <motion.div className="absolute inset-0" style={heroBgStyle}>
          <img
            src={HERO_IMG}
            alt="Interior space finished with statement wall art"
            className="h-full w-full object-cover"
          />
        </motion.div>
        <motion.div
          className="absolute inset-0 bg-[radial-gradient(circle_at_78%_18%,rgba(203,160,82,0.2),transparent_32%)]"
          style={heroGlowStyle}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#05060A] via-[#05060A]/76 to-[#05060A]/16" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#05060A] via-[#05060A]/30 to-transparent" />

        <div className="relative z-10 mx-auto flex min-h-[calc(100svh-5rem)] max-w-7xl flex-col justify-end px-6 pb-10 pt-16 md:px-12 md:pb-14 lg:pt-24">
          <motion.div
            initial={FADE_FROM_28}
            animate={FADE_TO_VISIBLE}
            transition={TRANSITION_HERO}
            className="max-w-3xl"
          >
            <p
              className="text-[11px] uppercase tracking-[0.34em] text-brand-gold"
              data-testid="hero-overline"
            >
              Printed locally in Coleraine
            </p>

            <p className="mt-4 font-heading text-[clamp(3.5rem,10vw,7.25rem)] leading-[0.88] tracking-[-0.05em] text-brand-text">
              AM Decor
            </p>

            <h1
              className="mt-5 max-w-[13ch] font-heading text-[clamp(2.2rem,5vw,4.4rem)] leading-[0.96] tracking-[-0.04em] text-brand-text"
              data-testid="hero-heading"
            >
              Print, murals, and signage with the finish of a fitted interior.
            </h1>

            <p
              className="mt-5 max-w-xl text-[15px] leading-relaxed text-white/74 md:text-[17px]"
              data-testid="hero-sub"
            >
              Canvas pieces, made-to-measure wall murals, and large-format graphics for homes,
              retail spaces, events, and branded rollout work across the UK.
            </p>

            <motion.div
              initial={FADE_FROM_18}
              animate={FADE_TO_VISIBLE}
              transition={TRANSITION_HERO_CTA}
              className="mt-9 flex flex-wrap gap-3"
            >
              <Link
                to="/canvas-category-landing"
                state={getRouteState(location)}
                className="inline-flex items-center gap-2 rounded-full bg-brand-gold px-7 py-3 text-[13px] font-medium tracking-[0.08em] text-brand-bg transition-colors duration-300 hover:bg-brand-gold-hover"
                data-testid="hero-canvas-cta"
              >
                Explore the collection <ArrowRight size={15} />
              </Link>
              <Link
                to="/request-quote"
                state={getRouteState(location)}
                className="inline-flex items-center gap-2 rounded-full border border-white/18 px-7 py-3 text-[13px] font-medium tracking-[0.08em] text-brand-text transition-colors duration-300 hover:border-brand-gold hover:text-brand-gold"
                data-testid="hero-quote-cta"
              >
                Request a quote
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            initial={FADE_FROM_26}
            animate={FADE_TO_VISIBLE}
            transition={TRANSITION_TRUST}
            className="mt-14 border-t border-white/10 pt-6"
            data-testid="trust-strip"
          >
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
              {trust.map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-3 text-sm text-white/74"
                  data-testid={`trust-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/12 bg-white/[0.03]">
                    <item.icon size={18} className="text-brand-gold" strokeWidth={1.6} />
                  </span>
                  <span className="tracking-[0.04em]">{item.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 md:py-28" data-testid="categories-section">
        <div className="mx-auto mb-10 flex max-w-7xl flex-wrap items-end justify-between gap-6 px-6 md:mb-14 md:px-12">
          <SectionFadeIn className="max-w-2xl">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-gold">
              Where It Lands
            </p>
            <h2 className="font-heading text-[2.2rem] leading-[0.94] tracking-[-0.04em] text-brand-text md:text-[3.5rem]">
              Three clean entry points into the studio.
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-brand-text-secondary md:text-base">
              Start with the type of surface you are shaping, then move into the material,
              scale, and finish that best fits the brief.
            </p>
          </SectionFadeIn>
          <GalleryNav />
        </div>

        <GalleryCarousel location={location} />

        <div className="mx-auto mt-10 max-w-7xl px-6 md:mt-12 md:px-12">
          <SectionFadeIn className="border-t border-white/8 pt-6">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <p className="text-[11px] uppercase tracking-[0.24em] text-white/44">
                Also produced
              </p>
              {quickLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-sm text-brand-text-secondary transition-colors duration-300 hover:text-brand-text"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </SectionFadeIn>
        </div>
      </section>

      <section
        className="relative overflow-hidden border-y border-white/[0.06] bg-[#0D121C] py-20 md:py-28"
        data-testid="selected-work-section"
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(203,160,82,0.12),transparent_35%)]" />
        <div className="relative mx-auto grid max-w-7xl gap-14 px-6 md:px-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-start">
          <SectionFadeIn className="lg:sticky lg:top-28">
            <div className="overflow-hidden">
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={STUDIO_IMG}
                  alt="Print studio materials and fitted wall graphics"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#05060A]/72 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <p className="text-[11px] uppercase tracking-[0.28em] text-brand-gold">
                    Material confidence
                  </p>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/72 md:text-[15px]">
                    Large-format print should feel considered in the room, not pasted on after
                    the fact.
                  </p>
                </div>
              </div>
            </div>
          </SectionFadeIn>

          <SectionFadeIn>
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-gold">
              How The Work Moves
            </p>
            <h2 className="mt-4 max-w-[11ch] font-heading text-[2.2rem] leading-[0.94] tracking-[-0.04em] text-brand-text md:text-[3.5rem]">
              Built for briefs, not generic baskets.
            </h2>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-brand-text-secondary md:text-base">
              The process is simple on purpose: understand the space, choose the right
              production route, then deliver something that looks like it belongs there.
            </p>

            <div className="mt-10 space-y-8">
              {processSteps.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={FADE_FROM_28}
                  whileInView={FADE_TO_VISIBLE}
                  viewport={VIEWPORT_ONCE_35}
                  transition={processStepTransition(index)}
                  className="border-t border-white/10 pt-6"
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:gap-6">
                    <span className="font-heading text-[2.25rem] leading-none text-white/20">
                      {step.number}
                    </span>
                    <div>
                      <h3 className="font-heading text-[1.55rem] leading-tight tracking-[-0.03em] text-brand-text">
                        {step.title}
                      </h3>
                      <p className="mt-3 max-w-xl text-sm leading-relaxed text-brand-text-secondary md:text-[15px]">
                        {step.copy}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-12 grid gap-4 sm:grid-cols-2">
              {trust.map((item) => (
                <div
                  key={`detail-${item.label}`}
                  className="border-t border-white/10 pt-4"
                >
                  <div className="flex items-center gap-3">
                    <item.icon size={18} className="text-brand-gold" strokeWidth={1.8} />
                    <p className="text-sm font-medium tracking-[0.03em] text-brand-text">
                      {item.label}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </SectionFadeIn>
        </div>
      </section>

      <section
        className="relative overflow-hidden py-20 md:py-24"
        data-testid="quote-cta-section"
      >
        <div className="absolute inset-0">
          <img
            src={CUSTOM_IMG}
            alt=""
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-[#05060A]/82" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(203,160,82,0.16),transparent_36%)]" />
        </div>

        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center md:px-12">
          <SectionFadeIn>
            <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-gold">
              Final Step
            </p>
            <h2 className="font-heading text-[2.3rem] leading-[0.94] tracking-[-0.04em] text-brand-text md:text-[4rem]">
              Bring us the wall, window, vehicle, or room.
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-white/72 md:text-base">
              Share dimensions, photos, material questions, or a deadline. We will shape the
              cleanest route to print and give you a clear quote without the back-and-forth.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/request-quote"
                state={getRouteState(location)}
                className="inline-flex items-center gap-2 rounded-full bg-brand-gold px-8 py-3.5 text-[13px] font-medium tracking-[0.08em] text-brand-bg transition-colors duration-300 hover:bg-brand-gold-hover"
                data-testid="bottom-quote-cta"
              >
                Request a free quote <ArrowRight size={15} />
              </Link>
              <Link
                to="/custom-order-landing"
                state={getRouteState(location)}
                className="inline-flex items-center gap-2 rounded-full border border-white/18 px-8 py-3.5 text-[13px] font-medium tracking-[0.08em] text-brand-text transition-colors duration-300 hover:border-brand-gold hover:text-brand-gold"
              >
                See custom projects
              </Link>
            </div>
          </SectionFadeIn>
        </div>
      </section>
    </>
  );
}
